import { parseWithZod } from '@conform-to/zod'
import {
  ActionFunctionArgs,
  json,
  LoaderFunctionArgs,
  MetaFunction,
  redirect,
} from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'

import { PrefecturePage } from '~/features'
import { TopFooter, TopHeader } from '~/features/common'
import { searchFormSchema } from '~/features/prefecture-page/components'
import {
  getAllFacilities,
  getAllTypes,
  searchAreas,
  searchPrefecture,
} from '~/models/master.server'
import { searchOffice } from '~/models/office.server'
import { Office, Prefecture } from '~/models/types'

const getPrefectureData = async (
  slug: string,
): Promise<{
  prefecture: Prefecture
} | null> => {
  const [prefecture] = await searchPrefecture({ slug })
  if (!prefecture) return null

  return { prefecture }
}

const getNewOffices = async (
  prefectureSlug: string,
): Promise<{ recentOffices: Office[]; count: number }> => {
  const { offices: recentOffices, count } = await searchOffice({
    sortOrder: 'RECENTLY_UPDATED',
    prefectureSlug,
  })
  return { recentOffices, count }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const submission = parseWithZod(formData, { schema: searchFormSchema })

  const prefectureSlug = formData.get('prefectureSlug')

  if (!prefectureSlug) {
    return submission.reply({
      formErrors: ['prefectureSlug is not defined'],
    })
  }

  if (submission.status !== 'success') {
    return submission.reply()
  }

  const queryParams = new URLSearchParams()

  formData.forEach((value, key) => {
    if (key !== 'prefectureSlug' && value.toString().trim() !== '') {
      if (queryParams.has(key)) {
        const existingValue = queryParams.get(key)
        queryParams.set(key, `${existingValue},${value.toString()}`)
      } else {
        queryParams.set(key, value.toString())
      }
    }
  })

  const searchUrl = `/prefectures/${prefectureSlug}/search?${queryParams.toString()}`

  return redirect(searchUrl)
}

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const { prefectureSlug } = params

  if (!prefectureSlug) throw new Response('Bad Request', { status: 400 })

  const data = await getPrefectureData(prefectureSlug)

  const prefecture = data?.prefecture

  if (!prefecture)
    throw new Response('Not Found', { status: 404, statusText: 'Not Found.' })

  const { recentOffices: newOffices, count } = await getNewOffices(
    prefecture.slug,
  )

  const [areasInPrefecture, allTypes, allFacilities] = await Promise.all([
    searchAreas({ prefectureId: prefecture.id }),
    getAllTypes(),
    getAllFacilities(),
  ])

  return json({
    prefecture,
    areasInPrefecture,
    allTypes,
    allFacilities,
    newOffices,
    count,
  })
}

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const prefectureName = data?.prefecture.name
  const titlePrefix = prefectureName ? prefectureName : ''
  const parentMeta = matches
    .flatMap((match) => match.meta ?? [])
    .filter((meta) => !('title' in meta))
  return [
    ...parentMeta,
    {
      title: titlePrefix + ' | シェアオフィス コワーキングスペース検索サイト',
    },
  ]
}

export default function Page() {
  const {
    areasInPrefecture,
    prefecture,
    allTypes,
    allFacilities,
    newOffices: jsonifiedNewOffices,
    count,
  } = useLoaderData<typeof loader>()

  // 新着物件の日付データも変換
  const newOffices: Office[] = jsonifiedNewOffices.map((office) => ({
    ...office,
    spaces: office.spaces.map((space) => ({
      ...space,
      createdAt: new Date(space.createdAt),
    })),
  }))

  return (
    <div className="flex min-h-screen flex-col">
      <TopHeader />
      <div className="flex-grow pt-[58px]">
        <PrefecturePage
          areas={areasInPrefecture}
          facilities={allFacilities}
          newOffices={newOffices}
          newOfficesCount={count}
          officeTypes={allTypes}
          prefecture={prefecture}
        />
      </div>
      <TopFooter />
    </div>
  )
}
